import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import { logError } from '../logError'

dayjs.extend(customParseFormat)
dayjs.extend(isBetween)

const DATE_TAB_MATRICES: { [key: string]: any } = {
    fullScreen: {
        totalDays: 6,
        nextDays: 3,
        prevDays: 3
    },
    splitScreen: {
        totalDays: 2,
        nextDays: 1,
        prevDays: 1
    }
}

export function getFormattedDate(date: any, outputFormat: any = null, inputFormat: any = null) {
    let newDate = 'invalid date'
    try {
        if (!date) {
            newDate = dayjs().format(outputFormat)
        } else if (inputFormat) {
            // If input format is provided, use it directly
            newDate = dayjs(date, inputFormat).format(outputFormat)
        } else {
            // Auto-detect input format by trying common formats
            // Order formats by most common/expected first
            const commonFormats = [
                'DD/MM/YYYY', // Most common in many countries
                'YYYY-MM-DD', // ISO format
                'MM/DD/YYYY', // US format
                'DD-MM-YYYY',
                'MM-DD-YYYY',
                'YYYY/MM/DD',
                'DD.MM.YYYY',
                'MM.DD.YYYY',
                'YYYY.MM.DD',
                'D/M/YYYY',
                'M/D/YYYY',
                'D-M-YYYY',
                'M-D-YYYY',
                'YYYY-M-D',
                'YYYY/M/D'
            ]

            let parsedDate = null
            for (const format of commonFormats) {
                const temp = dayjs(date, format)
                if (temp.isValid()) {
                    parsedDate = temp
                    break
                }
            }

            if (parsedDate) {
                newDate = parsedDate.format(outputFormat)
            } else {
                // If no format matches, try dayjs's default parsing
                const defaultParsed = dayjs(date)
                if (defaultParsed.isValid()) {
                    newDate = defaultParsed.format(outputFormat)
                }
            }
        }
    } catch (e) {
        newDate = 'invalid date'
        logError(e)
    }
    return newDate
}

export function getInitialDateRange({ thisDate: curDate, screenType }) {
    const { totalDays, prevDays } = DATE_TAB_MATRICES[screenType] || DATE_TAB_MATRICES['onward']
    const dDate = dayjs(curDate, 'DD/MM/YYYY')
    const diffDate = dDate.diff(dayjs(), 'day')
    let prevDate = dayjs()
    if (diffDate > 3) {
        prevDate = dayjs(dDate).subtract(prevDays, 'day')
    } else {
        prevDate = dayjs(dDate).subtract(diffDate, 'day')
    }
    const nextDate = dayjs(prevDate).add(totalDays, 'day')
    return `${dayjs(prevDate).format('DD-MM-YYYY')}_${dayjs(nextDate).format('DD-MM-YYYY')}`
}

export function getNewDateRange(dateRange, direction, screenType) {
    const { totalDays } = DATE_TAB_MATRICES[screenType] || DATE_TAB_MATRICES['onward']
    const dates = dateRange.split('_')
    let prevDate
    let nextDate
    if (direction === 'right') {
        prevDate = dayjs(dates[1], 'DD-MM-YYYY').add(1, 'day').format('DD-MM-YYYY')
        nextDate = dayjs(prevDate, 'DD-MM-YYYY').add(totalDays, 'day').format('DD-MM-YYYY')
        return `${prevDate}_${nextDate}`
    }
    const diffDate = dayjs(dates[0], 'DD-MM-YYYY').diff(dayjs(), 'day')
    if (diffDate > totalDays + 1) {
        prevDate = dayjs(dayjs(dates[0], 'DD-MM-YYYY'))
            .subtract(totalDays + 1, 'day')
            .format('DD-MM-YYYY')
    } else {
        prevDate = dayjs(dayjs(dates[0], 'DD-MM-YYYY')).subtract(diffDate, 'day').format('DD-MM-YYYY')
    }
    nextDate = dayjs(prevDate, 'DD-MM-YYYY').add(totalDays, 'day').format('DD-MM-YYYY')
    return `${prevDate}_${nextDate}`
}

export function getInitialMonthRange(departDate) {
    const departureDate = dayjs(departDate, 'DD-MM-YYYY')
    const today = dayjs()
    const diffMonth = departureDate.diff(today, 'month')
    const sub = !diffMonth ? 0 : 1
    const add = 1
    const prevMonth = !diffMonth
        ? dayjs(dayjs(today, 'DD-MM-YYYY')).subtract(sub, 'month').format('DD-MM-YYYY')
        : dayjs(dayjs(departureDate, 'DD-MM-YYYY')).subtract(sub, 'month').format('DD-MM-YYYY')
    const nextMonth = dayjs(dayjs(departureDate, 'DD-MM-YYYY').add(add, 'month')).endOf('month').format('DD-MM-YYYY')
    const defaultDateFormat = `${prevMonth}_${nextMonth}`
    return defaultDateFormat
}
export function getMonthRange(month, direction, monthCount = 3) {
    let prevMonth
    let nextMonth
    let parseMonth = dayjs(getFormattedDate(month, 'DD-MM-YYYY'), 'DD-MM-YYYY')
    const today = dayjs()
    const diffMonth = parseMonth.diff(today, 'month')
    if (direction === 'right') {
        prevMonth = dayjs(dayjs(parseMonth, 'DD-MM-YYYY')).add(2, 'month').format('DD-MM-YYYY')
        nextMonth = dayjs(dayjs(parseMonth, 'DD-MM-YYYY').add(monthCount, 'month')).endOf('month').format('DD-MM-YYYY')
    } else {
        prevMonth = !diffMonth
            ? dayjs(dayjs(today, 'DD-MM-YYYY')).format('DD-MM-YYYY')
            : dayjs(dayjs(parseMonth, 'DD-MM-YYYY')).format('DD-MM-YYYY')
        nextMonth = dayjs(dayjs(parseMonth, 'DD-MM-YYYY').add(1, 'month')).endOf('month').format('DD-MM-YYYY')
    }
    return `${prevMonth}_${nextMonth}`
}

export function minutesToHnM(minutes) {
    const hours = Math.floor(minutes / 60)
    const remMin = minutes % 60
    return remMin > 0 ? `${hours}h ${remMin}m` : `${hours}h`
}

export function dayDiff(days, to) {
    const dDate = dayjs(getFormattedDate(days, 'DD-MM-YYYY'), 'DD-MM-YYYY')
    const toDate = dayjs(getFormattedDate(to, 'DD-MM-YYYY'), 'DD-MM-YYYY')
    const diffDate = dDate.diff(toDate, 'day')
    return diffDate
}

export function differenceInHours(from, to) {
    const H1 = dayjs(from)
    const H2 = dayjs(to)
    const diff = H2.diff(H1, 'hours', true)
    return diff > 0 ? Math.floor(diff) : Math.ceil(diff)
}

export function differenceInMinutes(timestampLeft: number, timestampRight: number): number {
    const MILLISECONDS_IN_MINUTE = 60_000
    const diff = (timestampLeft - timestampRight) / MILLISECONDS_IN_MINUTE
    return diff > 0 ? Math.floor(diff) : Math.ceil(diff)
}
export function differenceInMilliseconds(timestampLeft: number, timestampRight: number): number {
    return timestampLeft - timestampRight
}
export function dateDiff(date1, date2, format = 'DD-MM-YYYY') {
    const dDate1 = dayjs(date1, format)
    const dDate2 = dayjs(date2, format)
    const diffDate = dDate2.diff(dDate1, 'day')
    return diffDate
}

export function yearDiff(date1, date2, format = 'DD-MM-YYYY') {
    const dDate1 = dayjs(date1, format)
    const dDate2 = dayjs(date2, format)
    const diffYear = dDate2.diff(dDate1, 'year', true)
    return diffYear
}

export const getDateAfterNDays = (d, n) => {
    const date = new Date(d)
    date.setDate(date.getDate() + n)
    const formattedDate = dayjs(date).format('DD/MM/YYYY')
    return formattedDate
}

export const validSearchQueryDateFormats = ['DD/MM/YYYY', 'D/M/YYYY', 'D/MM/YYYY', 'DD/M/YYYY']

/**
 * Checks if a date string is valid for the format 'DD/MM/YYYY' using dayjs.
 * @param dateStr string date in 'DD/MM/YYYY' format
 * @returns boolean
 */
export function isValidSearchQueryDate(dateStr: string): boolean {
    return validSearchQueryDateFormats.some(format => dayjs(dateStr, format).isValid())
}

/**
 * Format a date string to a readable format
 * @param dateString - The date string to format
 * @returns Formatted date string (e.g., "January 1, 2023")
 */
export const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A'

    try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    } catch (error) {
        console.error('Error formatting date:', error)
        return 'Invalid date'
    }
}

/**
 * Format a time string to a readable format
 * @param dateString - The date string containing the time
 * @returns Formatted time string (e.g., "2:30 PM")
 */
export const formatTime = (dateString: string): string => {
    if (!dateString) return 'N/A'

    try {
        const date = new Date(dateString)
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        })
    } catch (error) {
        console.error('Error formatting time:', error)
        return 'Invalid time'
    }
}

/**
 * Format a duration in minutes to a readable format
 * @param minutes - The duration in minutes
 * @returns Formatted duration string (e.g., "2h 30m")
 */
export const formatDuration = (minutes: number | string): string => {
    if (minutes === undefined || minutes === null) return 'N/A'

    try {
        const mins = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes
        const hours = Math.floor(mins / 60)
        const remainingMins = mins % 60

        if (hours > 0 && remainingMins > 0) {
            return `${hours}h ${remainingMins}m`
        } else if (hours > 0) {
            return `${hours}h`
        } else {
            return `${remainingMins}m`
        }
    } catch (error) {
        console.error('Error formatting duration:', error)
        return 'Invalid duration'
    }
}

/**
 * Calculate the difference in minutes between two dates
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns Difference in minutes
 */
export const getDurationInMinutes = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0

    try {
        const start = new Date(startDate)
        const end = new Date(endDate)
        const diffInMs = end.getTime() - start.getTime()
        return Math.round(diffInMs / (1000 * 60))
    } catch (error) {
        console.error('Error calculating duration:', error)
        return 0
    }
}

/**
 * Check if a date is in the past
 * @param dateString - The date string to check
 * @returns True if the date is in the past, false otherwise
 */
export const isPastDate = (dateString: string): boolean => {
    if (!dateString) return false

    try {
        const date = new Date(dateString)
        const now = new Date()
        return date < now
    } catch (error) {
        console.error('Error checking if date is in past:', error)
        return false
    }
}

/**
 * Format a date to YYYY-MM-DD format
 * @param dateString - The date string to format
 * @returns Date string in YYYY-MM-DD format
 */
export const formatDateYMD = (dateString: string): string => {
    if (!dateString) return ''

    try {
        const date = new Date(dateString)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    } catch (error) {
        console.error('Error formatting date to YYYY-MM-DD:', error)
        return ''
    }
}
